import React from 'react';

function formatMinutesPerSession(totalSeconds, sessionCount) {
  const seconds = Math.max(0, Number(totalSeconds) || 0);
  const sessions = Math.max(0, Number(sessionCount) || 0);
  if (sessions <= 0) return '0m';
  const minutes = Math.round((seconds / 60) / sessions);
  return `${minutes}m`;
}

function formatTimeOfDay(minuteOfDay) {
  const safeMinute = Math.max(0, Math.min(1439, Number(minuteOfDay) || 0));
  const hour = Math.floor(safeMinute / 60);
  const minute = safeMinute % 60;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function pointY(point, height, pad) {
  const plotHeight = height - pad.top - pad.bottom;
  const timeOfDayMinute = Math.max(0, Math.min(1439, Number(point?.timeOfDayMinute ?? 0)));
  const yRatio = timeOfDayMinute / 1439;
  return pad.top + yRatio * plotHeight;
}

function buildJourneyPath(points, maxMinute, width, height, pad) {
  if (!Array.isArray(points) || points.length === 0) return '';

  const plotWidth = width - pad.left - pad.right;

  return points
    .map((point, idx) => {
      const minute = Math.max(0, Number(point?.minute || 0));
      const x = pad.left + ((minute / Math.max(1, maxMinute)) * plotWidth);
      const y = pointY(point, height, pad);
      return `${idx === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(' ');
}

function journeyColor(index) {
  const palette = ['#0ea5e9', '#22c55e', '#f97316', '#eab308', '#14b8a6', '#f43f5e', '#6366f1', '#84cc16'];
  return palette[index % palette.length];
}

export default function JourneyDashboardSection({
  loading,
  error,
  days,
  journeys,
  eventAxis,
  eventTypeCounts,
  completionTrend,
  warningSignals,
  writingSessions,
  writingByUser,
  onRefresh,
  onDaysChange,
  onExport,
}) {
  const chartWidth = 920;
  const chartHeight = 360;
  const pad = { top: 22, right: 18, bottom: 42, left: 220 };
  const lines = Array.isArray(journeys) ? journeys : [];
  const completionTrendRows = Array.isArray(completionTrend) ? completionTrend : [];
  const maxMinute = Math.max(
    1,
    ...lines.flatMap((line) => (Array.isArray(line?.points) ? line.points.map((p) => Number(p?.minute || 0)) : [0])),
  );
  const trendWidth = 920;
  const trendHeight = 280;
  const trendPad = { top: 20, right: 52, bottom: 44, left: 52 };
  const trendPlotWidth = trendWidth - trendPad.left - trendPad.right;
  const trendPlotHeight = trendHeight - trendPad.top - trendPad.bottom;
  const maxStartedCount = Math.max(10, ...completionTrendRows.map((row) => Number(row?.startedCount || 0)));
  const trendX = (idx) => {
    if (completionTrendRows.length <= 1) return trendPad.left;
    return trendPad.left + ((idx / (completionTrendRows.length - 1)) * trendPlotWidth);
  };
  const trendYRate = (ratePct) => {
    const safeRate = Math.max(0, Math.min(100, Number(ratePct) || 0));
    return trendPad.top + ((100 - safeRate) / 100) * trendPlotHeight;
  };
  const trendYStarted = (count) => {
    const safeCount = Math.max(0, Number(count) || 0);
    return trendPad.top + (1 - (safeCount / Math.max(1, maxStartedCount))) * trendPlotHeight;
  };
  const trendRatePath = completionTrendRows.map((row, idx) => {
    const x = trendX(idx);
    const y = trendYRate(row?.completionRatePct || 0);
    return `${idx === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
  }).join(' ');
  const trendAvgPath = completionTrendRows.map((row, idx) => {
    const x = trendX(idx);
    const y = trendYRate(row?.movingAvg7dPct || 0);
    return `${idx === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
  }).join(' ');

  const topEvents = (Array.isArray(eventTypeCounts) ? eventTypeCounts : []).slice(0, 8);
  const topEventMax = Math.max(1, ...topEvents.map((e) => Number(e.count || 0)), 1);
  const warningCards = [
    {
      key: 'verifyWithoutLogin',
      title: 'Verified but never logged in',
      description: 'Users passed verification but no login completed after that moment.',
      data: warningSignals?.verifyWithoutLogin,
      sampleIdKey: 'session_id',
      sampleLabel: 'Session',
    },
    {
      key: 'scaffoldWithoutWriterSession',
      title: 'Scaffold created but session never started',
      description: 'Story scaffold flow completed without a following writer session start.',
      data: warningSignals?.scaffoldWithoutWriterSession,
      sampleIdKey: 'session_id',
      sampleLabel: 'Session',
    },
    {
      key: 'completedWriterWithoutReflection',
      title: 'Writing completed but reflection dropped',
      description: 'Completed writer sessions with no reflection start in the next 45 minutes.',
      data: warningSignals?.completedWriterWithoutReflection,
      sampleIdKey: 'writing_session_id',
      sampleLabel: 'Writing',
    },
  ];

  const totalSessionOutcomes = Number(writingSessions?.completedCount || 0) + Number(writingSessions?.abandonedCount || 0);

  return (
    <div className="admin-journey">
      <div className="admin-card admin-card--spaced">
        <div className="admin-card-head admin-journey-head">
          <div>
            <h2 className="admin-card-title">User Journey Analytics</h2>
            <p className="admin-muted admin-no-margin">Minutes in session on X-axis, UTC time of day on Y-axis, one line per user journey session.</p>
          </div>
          <div className="admin-journey-controls">
            <label className="admin-journey-days-label" htmlFor="journey-days">Days</label>
            <select
              id="journey-days"
              className="admin-journey-select"
              value={days}
              onChange={(e) => onDaysChange(Number(e.target.value))}
              disabled={loading}
            >
              {[7, 14, 30, 60, 90].map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
            <button type="button" className="btn subtle admin-refresh-btn" onClick={onRefresh} disabled={loading}>
              {loading ? 'Loading…' : 'Refresh'}
            </button>
            <button type="button" className="btn subtle admin-refresh-btn" onClick={onExport} disabled={loading}>
              Export PDF
            </button>
          </div>
        </div>

        {error && <p className="admin-error">{error}</p>}

        <div className="admin-journey-kpis">
          <div className="admin-journey-kpi">
            <span className="admin-journey-kpi-label">Completed sessions</span>
            <strong className="admin-journey-kpi-value">{Number(writingSessions?.completedCount || 0)}</strong>
          </div>
          <div className="admin-journey-kpi">
            <span className="admin-journey-kpi-label">Broken sessions</span>
            <strong className="admin-journey-kpi-value">{Number(writingSessions?.abandonedCount || 0)}</strong>
          </div>
          <div className="admin-journey-kpi">
            <span className="admin-journey-kpi-label">Mobile writing per session</span>
            <strong className="admin-journey-kpi-value">{formatMinutesPerSession(writingSessions?.mobileSeconds || 0, totalSessionOutcomes)}</strong>
          </div>
          <div className="admin-journey-kpi">
            <span className="admin-journey-kpi-label">Web writing per session</span>
            <strong className="admin-journey-kpi-value">{formatMinutesPerSession(writingSessions?.webSeconds || 0, totalSessionOutcomes)}</strong>
          </div>
          <div className="admin-journey-kpi">
            <span className="admin-journey-kpi-label">Outcome completion rate</span>
            <strong className="admin-journey-kpi-value">
              {totalSessionOutcomes > 0
                ? `${Math.round((Number(writingSessions?.completedCount || 0) / totalSessionOutcomes) * 100)}%`
                : '0%'}
            </strong>
          </div>
        </div>
      </div>

      <div className="admin-table-card admin-table-card--spaced">
        <div className="admin-table-head">
          <h2 className="admin-table-title">Session completion engagement</h2>
          <span className="admin-muted">Daily completion rate and started sessions</span>
        </div>

        {completionTrendRows.length === 0 ? (
          <p className="admin-empty">No writing session trend data in this window.</p>
        ) : (
          <div className="admin-journey-chart-wrap">
            <svg className="admin-journey-chart" viewBox={`0 0 ${trendWidth} ${trendHeight}`} role="img" aria-label="Daily completion rate with 7-day average and started sessions">
              <rect x="0" y="0" width={trendWidth} height={trendHeight} fill="transparent" />

              {[0, 25, 50, 75, 100].map((tick) => {
                const y = trendYRate(tick);
                return (
                  <g key={`rate-${tick}`}>
                    <line x1={trendPad.left} y1={y} x2={trendWidth - trendPad.right} y2={y} className="admin-journey-grid" />
                    <text x={trendPad.left - 8} y={y + 4} textAnchor="end" className="admin-journey-axis-label">{`${tick}%`}</text>
                  </g>
                );
              })}

              {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
                const idx = Math.min(completionTrendRows.length - 1, Math.max(0, Math.round(ratio * Math.max(0, completionTrendRows.length - 1))));
                const row = completionTrendRows[idx];
                const x = trendX(idx);
                const label = row?.day ? String(row.day).slice(5) : '';
                return (
                  <g key={`trend-x-${ratio}`}>
                    <line x1={x} y1={trendPad.top} x2={x} y2={trendPad.top + trendPlotHeight} className="admin-journey-grid-x" />
                    <text x={x} y={trendHeight - trendPad.bottom + 16} textAnchor="middle" className="admin-journey-axis-label">{label}</text>
                  </g>
                );
              })}

              {completionTrendRows.map((row, idx) => {
                const x = trendX(idx);
                const barWidth = Math.max(3, trendPlotWidth / Math.max(12, completionTrendRows.length * 1.7));
                const y = trendYStarted(row?.startedCount || 0);
                const h = Math.max(1, (trendPad.top + trendPlotHeight) - y);
                return (
                  <rect
                    key={`bar-${row?.day || idx}`}
                    x={x - (barWidth / 2)}
                    y={y}
                    width={barWidth}
                    height={h}
                    fill={row?.lowConfidence ? '#fca5a5' : '#cbd5e1'}
                    opacity="0.65"
                  />
                );
              })}

              <path d={trendRatePath} fill="none" stroke="#0ea5e9" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" />
              <path d={trendAvgPath} fill="none" stroke="#0f766e" strokeWidth="2" strokeDasharray="5 4" strokeLinecap="round" strokeLinejoin="round" />

              {completionTrendRows.map((row, idx) => {
                const x = trendX(idx);
                const y = trendYRate(row?.completionRatePct || 0);
                return (
                  <g key={`dot-${row?.day || idx}`}>
                    <circle cx={x} cy={y} r="2.8" fill="#0ea5e9" />
                    {row?.lowConfidence ? <circle cx={x} cy={y} r="4.2" fill="none" stroke="#dc2626" strokeWidth="1.3" /> : null}
                  </g>
                );
              })}

              <line x1={trendPad.left} y1={trendPad.top + trendPlotHeight} x2={trendWidth - trendPad.right} y2={trendPad.top + trendPlotHeight} className="admin-journey-axis" />
              <line x1={trendPad.left} y1={trendPad.top} x2={trendPad.left} y2={trendPad.top + trendPlotHeight} className="admin-journey-axis" />
              <line x1={trendWidth - trendPad.right} y1={trendPad.top} x2={trendWidth - trendPad.right} y2={trendPad.top + trendPlotHeight} className="admin-journey-axis" />

              {[0, 0.5, 1].map((ratio) => {
                const count = Math.round(ratio * maxStartedCount);
                const y = trendYStarted(count);
                return (
                  <text key={`started-${ratio}`} x={trendWidth - trendPad.right + 8} y={y + 4} className="admin-journey-axis-label">{count}</text>
                );
              })}

              <text x={trendPad.left} y={trendPad.top - 6} className="admin-journey-axis-label">Completion rate</text>
              <text x={trendWidth - trendPad.right + 8} y={trendPad.top - 6} className="admin-journey-axis-label">Started sessions</text>
            </svg>
            <p className="admin-muted admin-no-margin">Blue line: daily completion rate. Teal dashed: 7-day moving average. Gray bars: started sessions. Red outlined points indicate fewer than 10 started sessions.</p>
          </div>
        )}
      </div>

      <div className="admin-table-card admin-table-card--spaced">
        <div className="admin-table-head">
          <h2 className="admin-table-title">Journey timeline</h2>
          <span className="admin-muted">Each line is one session</span>
        </div>

        <div className="admin-journey-chart-wrap">
          <div className="admin-journey-graph-warnings" role="status" aria-live="polite">
            {warningCards.map((card) => {
              const count = Number(card?.data?.count || 0);
              return (
                <div key={`graph-warning-${card.key}`} className={`admin-journey-graph-warning ${count > 0 ? 'is-active' : ''}`}>
                  <span className="admin-journey-graph-warning__icon" aria-hidden>i</span>
                  <span className="admin-journey-graph-warning__text">{card.title}</span>
                  <strong className="admin-journey-graph-warning__count">{count}</strong>
                </div>
              );
            })}
          </div>

          {lines.length === 0 ? (
            <p className="admin-empty">No events in this window.</p>
          ) : (
            <svg className="admin-journey-chart" viewBox={`0 0 ${chartWidth} ${chartHeight}`} role="img" aria-label="User journey timeline chart">
              <rect x="0" y="0" width={chartWidth} height={chartHeight} fill="transparent" />

              {[0, 360, 720, 1080, 1439].map((timeMinute) => {
                const y = pointY({ timeOfDayMinute: timeMinute }, chartHeight, pad);
                return (
                  <g key={`y-${timeMinute}`}>
                    <line x1={pad.left} y1={y} x2={chartWidth - pad.right} y2={y} className="admin-journey-grid" />
                    <text x={pad.left - 10} y={y + 4} textAnchor="end" className="admin-journey-axis-label admin-journey-event-label">{formatTimeOfDay(timeMinute)}</text>
                  </g>
                );
              })}

              <line x1={pad.left} y1={chartHeight - pad.bottom} x2={chartWidth - pad.right} y2={chartHeight - pad.bottom} className="admin-journey-axis" />
              <line x1={pad.left} y1={pad.top} x2={pad.left} y2={chartHeight - pad.bottom} className="admin-journey-axis" />

              {[0, 0.25, 0.5, 0.75, 1].map((tick) => {
                const minute = Math.round(tick * maxMinute);
                const x = pad.left + tick * (chartWidth - pad.left - pad.right);
                return (
                  <g key={`x-${tick}`}>
                    <line x1={x} y1={pad.top} x2={x} y2={chartHeight - pad.bottom} className="admin-journey-grid-x" />
                    <text x={x} y={chartHeight - pad.bottom + 14} textAnchor="middle" className="admin-journey-axis-label">{minute}m</text>
                  </g>
                );
              })}

              {lines.map((line, idx) => {
                const color = journeyColor(idx);
                const d = buildJourneyPath(line.points, maxMinute, chartWidth, chartHeight, pad);
                return d ? <path key={line.sessionId || idx} d={d} style={{ stroke: color }} className="admin-journey-line" /> : null;
              })}

              {lines.map((line, idx) => {
                const color = journeyColor(idx);
                return (Array.isArray(line?.points) ? line.points : []).map((point, pointIdx) => {
                  const minute = Math.max(0, Number(point?.minute || 0));
                  const x = pad.left + ((minute / Math.max(1, maxMinute)) * (chartWidth - pad.left - pad.right));
                  const y = pointY(point, chartHeight, pad);
                  const eventType = String(point?.eventType || 'event');
                  return (
                    <circle key={`pt-${line.sessionId || idx}-${pointIdx}`} cx={x} cy={y} r="3.2" fill={color} opacity="0.95">
                      <title>{`${eventType} at ${formatTimeOfDay(point?.timeOfDayMinute)}`}</title>
                    </circle>
                  );
                });
              })}

              {lines.slice(0, 6).map((line, idx) => (
                <g key={`legend-${line.sessionId || idx}`}>
                  <line
                    x1={chartWidth - pad.right - 170}
                    y1={pad.top + (idx * 16)}
                    x2={chartWidth - pad.right - 150}
                    y2={pad.top + (idx * 16)}
                    style={{ stroke: journeyColor(idx), strokeWidth: 2 }}
                  />
                  <text
                    x={chartWidth - pad.right - 146}
                    y={pad.top + (idx * 16) + 4}
                    className="admin-journey-axis-label"
                  >
                    {`U${line.userId ?? 'n/a'} · ${String(line.sessionId || '').slice(0, 8)}`}
                  </text>
                </g>
              ))}
            </svg>
          )}
        </div>
      </div>

      <div className="admin-table-card admin-table-card--spaced">
        <div className="admin-table-head">
          <h2 className="admin-table-title">Top journey events</h2>
          <span className="admin-muted">Event volume</span>
        </div>

        <div className="admin-journey-bars">
          {topEvents.length === 0 && <p className="admin-empty">No event data in this window.</p>}
          {topEvents.map((row) => {
            const count = Number(row.count || 0);
            const widthPercent = Math.max(2, Math.round((count / topEventMax) * 100));
            return (
              <div key={row.eventType} className="admin-journey-bar-row">
                <div className="admin-journey-bar-meta">
                  <span className="admin-journey-bar-name">{row.eventType}</span>
                  <span className="admin-journey-bar-count">{count}</span>
                </div>
                <div className="admin-journey-bar-track">
                  <div className="admin-journey-bar-fill" style={{ width: `${widthPercent}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="admin-table-card admin-table-card--spaced">
        <div className="admin-table-head">
          <h2 className="admin-table-title">Time spent per user by platform</h2>
          <span className="admin-muted">Mobile vs web writing time</span>
        </div>

        {Array.isArray(writingByUser) && writingByUser.length > 0 ? (
          <table className="admin-table">
            <thead>
              <tr>
                <th className="admin-th admin-th-left">User</th>
                <th className="admin-th">Completed</th>
                <th className="admin-th">Broken</th>
                <th className="admin-th">Mobile / session</th>
                <th className="admin-th">Web / session</th>
                <th className="admin-th admin-th-right">Avg / session</th>
              </tr>
            </thead>
            <tbody>
              {writingByUser.map((row, index) => {
                const rowSessionCount = Number(row.completedCount || 0) + Number(row.abandonedCount || 0);
                return (
                  <tr key={row.userId} className={index === 0 ? 'admin-row-first' : 'admin-row'}>
                    <td className="admin-td admin-td-left admin-mono">#{row.userId}</td>
                    <td className="admin-td admin-td-muted">{row.completedCount}</td>
                    <td className="admin-td admin-td-muted">{row.abandonedCount}</td>
                    <td className="admin-td admin-td-muted">{formatMinutesPerSession(row.mobileSeconds, rowSessionCount)}</td>
                    <td className="admin-td admin-td-muted">{formatMinutesPerSession(row.webSeconds, rowSessionCount)}</td>
                    <td className="admin-td admin-td-right admin-mono">{formatMinutesPerSession(row.totalSeconds, rowSessionCount)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="admin-empty">No writing-session stats available in this window.</p>
        )}
      </div>
    </div>
  );
}
